import { DateTime } from 'luxon'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLiveQuery } from 'dexie-react-hooks'

import { offlineDb, type SyncIssue } from '../../offline/db'
import { SyncStatus } from '../../DTOs/Sync/SyncDtos'
import { cachedMachineName, describeShiftId } from '../../offline/describeShiftId'
import Button from '../Buttons/Button'

function issueContext(issue: SyncIssue): string {
  const row = (issue.clientData ?? issue.serverState ?? {}) as {
    plantId?: number
    truckId?: number
    plant?: { plantRef?: string }
    loads?: number
    hour?: number
    startTime?: unknown
  }

  const machine = row.plant?.plantRef ?? cachedMachineName(row.plantId ?? row.truckId)
  const shift = describeShiftId(issue.shiftId)
  const detail = row.loads != null ? `${row.loads} loads` : undefined
  const hour = row.hour != null ? `hour ${row.hour}` : undefined
  const logged = issue.createdAt ? DateTime.fromMillis(issue.createdAt).toFormat('HH:mm') : undefined

  const parts = [machine, detail, hour, shift, logged && `logged ${logged}`].filter(Boolean)
  return parts.length ? ` (${parts.join(', ')})` : ''
}

function issueLabel(issue: SyncIssue): string {
  const entityName = issue.entity === 'diggingPattern' ? 'digging location'
    : issue.entity === 'loading' ? 'truck load'
    : issue.entity === 'geoRehandle' ? 'rehandle/crushing entry'
    : issue.entity === 'completeShift' ? 'shift completion'
    : issue.entity === 'explosiveMovement' ? 'explosives movement'
    : issue.entity === 'firing' ? 'firing record'
    : issue.entity === 'misfire' ? 'misfire report'
    : issue.entity === 'consumableMovement' ? 'consumables issue'
    : issue.entity === 'componentAction' ? 'bit or steel change'
    : issue.entity === 'drilledHole' ? 'drilled hole'
    : issue.entity === 'holeDip' ? 'dip'
    : issue.entity === 'holeCharge' ? 'hole charge'
    : issue.entity
  const about = issueContext(issue)
  if (issue.status === SyncStatus.Conflict) return `A ${entityName} conflict${about}: it was edited by someone else`
  if (issue.status === SyncStatus.ShiftLocked) return `A ${entityName} change${about} was rejected: shift is locked`
  if (issue.status === SyncStatus.Error) return `A ${entityName} change${about} failed: ${typeof issue.serverState === 'string' ? issue.serverState : 'server error'}`
  return `A ${entityName} change${about} couldn't be applied because the record no longer exists on the server (it may have been deleted). Re-enter it if it's still needed, then dismiss this.`
}

async function dismissIssue(id: number) {
  await offlineDb.syncIssues.delete(id)
}

async function dismissAll() {
  await offlineDb.syncIssues.clear()
}

interface SyncIssuesModalProps {
  onClose: () => void
  onResolveConflict: (issue: SyncIssue) => void
}

const SyncIssuesModal = ({ onClose, onResolveConflict }: SyncIssuesModalProps) => {
  const issues = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), []) ?? []

  return (
    <div>
      <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
        <FontAwesomeIcon icon={faTriangleExclamation} className="mr-3 text-theme-danger" />
        {issues.length} sync {issues.length === 1 ? 'issue' : 'issues'} need your attention
      </h2>

      {issues.length === 0 ? (
        <p className="!text-theme-text">Nothing left to deal with.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {issues.map(issue => (
            <li
              key={issue.id}
              className="flex items-start justify-between gap-3 border-b border-theme-border pb-2 last:border-b-0"
            >
              <span className="text-sm text-theme-text">{issueLabel(issue)}</span>
              <div className="flex items-center gap-2 shrink-0">
                {issue.status === SyncStatus.Conflict && (
                  <Button variant="primary" size="sm" onClick={() => onResolveConflict(issue)}>Resolve</Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => dismissIssue(issue.id!)}>Dismiss</Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex justify-end gap-2 mt-6">
        {issues.length > 1 && (
          <Button variant="danger" onClick={dismissAll}>Dismiss all</Button>
        )}
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  )
}

export default SyncIssuesModal
